import { useEffect, useState } from 'react';
import { Button, Modal, Textarea } from '@adop/ui';
import type { RestaurantStatus } from '@adop/shared';
import { Alert } from '../../components/Alert';

export interface AdminStatusModalProps {
  open: boolean;
  restaurantName: string;
  nextStatus: RestaurantStatus;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Shared suspend/reactivate confirmation. The reason is optional by contract and
 * is forwarded to POST /admin/restaurants/:id/status when provided.
 */
export function AdminStatusModal({
  open,
  restaurantName,
  nextStatus,
  busy,
  error,
  onClose,
  onConfirm,
}: AdminStatusModalProps) {
  const [reason, setReason] = useState('');
  const suspending = nextStatus === 'suspended';
  const action = suspending ? 'Suspend' : 'Reactivate';

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      closeOnBackdrop={!busy}
      closeOnEscape={!busy}
      title={`${action} ${restaurantName}?`}
      description={
        suspending
          ? 'The storefront and ordering will stop for every location of this restaurant.'
          : 'The restaurant will be able to take orders again.'
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={suspending ? 'danger' : 'primary'}
            loading={busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {action} restaurant
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert>{error}</Alert> : null}
        <Textarea
          label="Reason (optional)"
          hint="Stored on the audit entry so other admins can see why."
          value={reason}
          maxLength={500}
          onChange={(event) => setReason(event.target.value)}
          placeholder={suspending ? 'e.g. repeated chargebacks' : 'e.g. issue resolved'}
        />
      </div>
    </Modal>
  );
}
